/**
 * Copying and downloading the marks listed on a brand page.
 *
 * Both actions have to go through /api/screenshot rather than touching
 * curated.prefetch.io directly, for two separate reasons:
 *
 *   - the CDN sends no `access-control-allow-origin`, so a cross-origin
 *     fetch() for the bytes is blocked outright;
 *   - `<a download href="https://other-origin/…">` silently loses its download
 *     attribute cross-origin, so the browser navigates to the image instead of
 *     saving it. Saving under our own filename means holding the blob anyway.
 *
 * That endpoint proxies any prefetch.io image (it started life serving
 * screenshots for the colour sampler) and passes the upstream content type
 * through, which is what lets an SVG come back as text below.
 *
 * Every entry point resolves to a short message for the page's notification
 * toast — including the failures, because a copy button that quietly does
 * nothing is worse than one that says it couldn't.
 */
import { trackEvent } from '~/composables/analytics'
import type { BrandLogo } from '~/server/utils/brands'

/** Same-origin URL for a CDN asset (see server/api/screenshot.get.ts). */
const proxied = (url: string): string => `/api/screenshot?url=${encodeURIComponent(url)}`

const fetchAsset = async (logo: BrandLogo): Promise<Response> => {
  const response = await fetch(proxied(logo.url))
  if (!response.ok) throw new Error(`Asset request failed (${response.status})`)
  return response
}

/**
 * Raster bytes as a PNG blob.
 *
 * The clipboard only accepts PNG for images, so a WEBP or JPG mark is redrawn
 * onto a canvas first. The image is loaded from the proxy too — a canvas
 * tainted by a cross-origin draw refuses to hand back a blob.
 */
const toPngBlob = (logo: BrandLogo): Promise<Blob> =>
  new Promise((resolve, reject) => {
    const image = new Image()
    image.crossOrigin = 'anonymous'
    image.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = image.naturalWidth
      canvas.height = image.naturalHeight
      const context = canvas.getContext('2d')
      if (!context) return reject(new Error('Canvas unavailable'))
      context.drawImage(image, 0, 0)
      canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Encoding failed'))), 'image/png')
    }
    image.onerror = () => reject(new Error('Image could not be loaded'))
    image.src = proxied(logo.url)
  })

/**
 * Put a logo on the clipboard, and say what landed there.
 *
 * SVG is copied as markup: it is what a designer or developer wants to paste,
 * and it survives the round trip losslessly. Everything else is copied as an
 * image, which is what a document or a chat window expects.
 *
 * The ClipboardItem is handed a *promise* rather than an awaited blob on
 * purpose — Safari discards a clipboard write that isn't started inside the
 * user gesture that triggered it, and awaiting the fetch first ends that
 * gesture. Chrome and Firefox accept the same form.
 */
export async function copyLogo(logo: BrandLogo, brand?: string): Promise<string> {
  // Which brand and which mark, on every outcome — the event name only says
  // what landed on the clipboard, not what it was.
  const params = { brand, logo: logo.label, format: logo.format }

  try {
    if (!navigator.clipboard) throw new Error('Clipboard unavailable')

    if (logo.format === 'SVG') {
      const markup = await fetchAsset(logo).then((response) => response.text())
      await navigator.clipboard.writeText(markup)
      trackEvent('Brand Page Logo Copied (svg)', params)
      return 'SVG code copied!'
    }

    if (typeof ClipboardItem === 'undefined' || !navigator.clipboard.write) {
      throw new Error('Image clipboard unsupported')
    }
    const png =
      logo.format === 'PNG'
        ? fetchAsset(logo).then((response) => response.blob())
        : toPngBlob(logo)
    await navigator.clipboard.write([new ClipboardItem({ 'image/png': png })])
    trackEvent('Brand Page Logo Copied (image)', params)
    return 'Logo copied!'
  } catch {
    // Falling back to the address is a real answer: it pastes into a browser, a
    // CSS `url()` or a colleague's message, all of which resolve to the asset.
    try {
      await navigator.clipboard?.writeText(logo.url)
      trackEvent('Brand Page Logo Copied (url)', params)
      return 'Logo URL copied!'
    } catch {
      // The one path where the visitor asked for something and got nothing —
      // worth seeing in the dashboards next to the copies that succeeded.
      trackEvent('Brand Page Logo Copy Failed', params)
      return "Couldn't copy that logo."
    }
  }
}

/** Save a logo under a readable name (see BrandLogo.filename). */
export async function downloadLogo(logo: BrandLogo, brand?: string): Promise<string> {
  const params = { brand, logo: logo.label, format: logo.format }

  try {
    const blob = await fetchAsset(logo).then((response) => response.blob())
    const href = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.style.display = 'none'
    link.href = href
    link.download = logo.filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    // Revoked on the next frame, not immediately: Safari reads the object URL
    // asynchronously after the click, and revoking in the same tick can cancel
    // the save.
    requestAnimationFrame(() => URL.revokeObjectURL(href))
    trackEvent(`Brand Page Logo Downloaded (${logo.format.toLowerCase()})`, params)
    return `Downloading ${logo.filename}`
  } catch {
    trackEvent('Brand Page Logo Download Failed', params)
    return "Couldn't download that logo."
  }
}
